/* prettier-ignore */

const MINUTES_PER_HOUR = 60;
const MIDNIGHT_END_HOUR = 24;
const MAX_SLOT_HOUR = 24;
const TIME_STRING_PATTERN = /^(\d{1,2}):(\d{2})$/;

/**
 * Resolves a stop's slot size in whole hours from its own stored `start`/`end`
 * pair (HRS-3374): ceil((end - start) / 60min). The stored slot is the source
 * of truth for a stop's grid footprint; callers fall back to the catalogue
 * `duration` when this returns `null`.
 *
 * Accepts the encodings seen at its call sites:
 * - numeric integer hours (raw kiosk routes)
 * - "HH:mm" strings (isRefactorRoute routes and rebuilt grid items)
 *
 * An `end` of hour 0 (`0` / `"00:00"`) is this grid's TERMINAL midnight, so it
 * is interpreted as 24:00. Returns `null` for missing/unparseable values or
 * non-positive differences.
 */
export function getSlotSizeHours(start: unknown, end: unknown): number | null {
  const startMinutes = parseSlotMinutes(start);
  const endMinutes = parseSlotMinutes(end);
  if (startMinutes === null || endMinutes === null)
    return null;

  const effectiveEndMinutes = endMinutes === 0 ? MIDNIGHT_END_HOUR * MINUTES_PER_HOUR : endMinutes;
  const diffMinutes = effectiveEndMinutes - startMinutes;
  if (diffMinutes <= 0)
    return null;

  return Math.ceil(diffMinutes / MINUTES_PER_HOUR);
}

function parseSlotMinutes(value: unknown): number | null {
  if (typeof value === 'number')
    return Number.isFinite(value) && value >= 0 && value <= MAX_SLOT_HOUR ? value * MINUTES_PER_HOUR : null;

  if (typeof value === 'string') {
    const match = TIME_STRING_PATTERN.exec(value.trim());
    if (!match)
      return null;

    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    if (hours > MAX_SLOT_HOUR || minutes > 59)
      return null;

    return hours * MINUTES_PER_HOUR + minutes;
  }

  return null;
}
